import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { Stack, Typography } from '@mui/material';
import { resolveTripTimeZone } from '../../../lib/tripTime';
import GuestExportSection from './GuestExportSection';
import RunOfShowExportSection from './RunOfShowExportSection';
import SeatingExportSection from './SeatingExportSection';
import type { GroupDetail } from '../../../types/groups';

type PrintTarget = 'guests' | 'seating' | 'runOfShow';

/**
 * "Something on paper." A host on site with a laptop or nothing gets the
 * guest list, the seating chart and the run of show as a CSV or a printed
 * page — no new file format, no PDF dependency.
 *
 * Exactly one section prints at a time: requesting a print sets which one,
 * which flips that section's `@media print` visibility on and every other
 * section's off (see PrintableSection), then opens the browser print dialog.
 *
 * The state change is committed with `flushSync` BEFORE `window.print()` is
 * called. A plain setState followed by print() races React's render — and a
 * `setTimeout(0)` does not close the race, it just makes it intermittent:
 * the timer can fire before a slow re-render (a large seating chart) has
 * painted, and the dialog opens on the wrong or an empty section.
 *
 * The flag is cleared on `afterprint`, and — because not every mobile print
 * path fires that event — also when the `print` media query stops matching.
 * A flag that stays armed is invisible on screen and is overwritten by the
 * next Print click anyway, so the fallback is belt and braces, not a fix
 * for a visible bug.
 */
export default function HostExportTab({ group }: { group: GroupDetail }) {
  const [printing, setPrinting] = useState<PrintTarget | null>(null);
  const { timeZone, isFallback } = resolveTripTimeZone(group.timeZone);

  useEffect(() => {
    const clear = () => setPrinting(null);
    const media = window.matchMedia('print');
    const onMedia = (event: MediaQueryListEvent) => {
      if (!event.matches) clear();
    };
    window.addEventListener('afterprint', clear);
    media.addEventListener('change', onMedia);
    return () => {
      window.removeEventListener('afterprint', clear);
      media.removeEventListener('change', onMedia);
    };
  }, []);

  const requestPrint = (target: PrintTarget) => {
    flushSync(() => setPrinting(target));
    window.print();
  };

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary" sx={{ '@media print': { display: 'none' } }}>
        Download a CSV for a spreadsheet, or print straight from here — built
        for the day of the event, when a laptop is the best case.
      </Typography>
      <GuestExportSection
        group={group}
        printing={printing === 'guests'}
        onPrint={() => requestPrint('guests')}
      />
      <SeatingExportSection
        group={group}
        printing={printing === 'seating'}
        onPrint={() => requestPrint('seating')}
      />
      <RunOfShowExportSection
        group={group}
        timeZone={timeZone}
        timeZoneIsFallback={isFallback}
        printing={printing === 'runOfShow'}
        onPrint={() => requestPrint('runOfShow')}
      />
    </Stack>
  );
}
